import { Inject, Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { UnespCoreEnvironment } from '@unesp/core'
import { identity, Observable } from 'rxjs'
import { ErrorHandlerService } from './error-handler.service'
import { DepartamentoLiberarDisciplina } from '../models/departamento-liberar-disciplina'
import { DepartamentoAprovarInscricao } from '../models/departamento-aprovar-inscricao'
import { PlanoEntregue } from '../models/plano-entregue'
import { StatusInscricao } from '../models/status-inscricao'
import { RelatorioEntregue } from '../models/relatorio-entregue'
import { DepartamentoDisciplinaLiberada } from '../models/departamento-disciplina-liberada'
import { DepartamentoAprovarPlano } from '../models/departamento-aprovar-plano'
import { DepartamentoHomologarRelatorio } from '../models/departamento-homologar-relatorio'
import { StatusResponse } from '../models/status-response'
import { DepartamentoAnalisarInscricao } from '../models/departamento-analisar-inscricao'
import { DepartamentoAnalisarInscricaoForm } from '../models/departamento-analisar-inscricao-form'
import { Relatorio } from '../models/relatorio'
import { RelatorioForm } from '../models/relatorio-form'
import { DepartamentoAnalisarPlano } from '../models/departamento-analisar-plano'
import { DepartamentoAnalisarPlanoForm } from '../models/departamento-analisar-plano-form'

@Injectable({
  providedIn: 'root',
})
export class DepartamentoService {
  private baseUrl: string

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    @Inject('environment') env: UnespCoreEnvironment
  ) {
    this.baseUrl = env.baseUrlApi
  }

  liberarDisciplina(
    idDepartamentoSecao: number,
    ano: number,
    semestre: number
  ): Observable<DepartamentoLiberarDisciplina[]> {
    return this.http
      .get<DepartamentoLiberarDisciplina[]>(
        `${this.baseUrl}/departamento/liberar-disciplina/${idDepartamentoSecao}/${ano}/${semestre}`
      )
      .pipe(identity, this.errorHandlerService.catchError())
  }

  incluirDisciplina(
    idDepartamentoSecao: number,
    ano: number,
    semestre: number,
    data: DepartamentoLiberarDisciplina
  ): Observable<StatusResponse> {
    return this.http
      .post<StatusResponse>(
        `${this.baseUrl}/departamento/incluir-disciplina/${idDepartamentoSecao}/${ano}/${semestre}`,
        data
      )
      .pipe(identity, this.errorHandlerService.catchError())
  }

  removerDisciplina(
    idDepartamentoSecao: number,
    ano: number,
    semestre: number,
    idDisciplina: number
  ): Observable<StatusResponse> {
    return this.http
      .delete<StatusResponse>(
        `${this.baseUrl}/departamento/remover-disciplina/${idDepartamentoSecao}/${ano}/${semestre}/${idDisciplina}`
      )
      .pipe(identity, this.errorHandlerService.catchError())
  }

  aprovarInscricao(
    idDepartamentoSecao: number,
    idUnidade: number,
    ano: number
  ): Observable<DepartamentoAprovarInscricao[]> {
    return this.http
      .get<DepartamentoAprovarInscricao[]>(
        `${this.baseUrl}/departamento/aprovar-inscricao/${idDepartamentoSecao}/${idUnidade}/${ano}`
      )
      .pipe(identity, this.errorHandlerService.catchError())
  }

  analisarInscricao(id: number): Observable<DepartamentoAnalisarInscricao> {
    return this.http
      .get<DepartamentoAnalisarInscricao>(`${this.baseUrl}/departamento/aprovar-inscricao/${id}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  registrarAnaliseInscricao(id: number, form: DepartamentoAnalisarInscricaoForm): Observable<StatusResponse> {
    return this.http
      .post<StatusResponse>(`${this.baseUrl}/departamento/aprovar-inscricao/${id}`, form)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  aprovarPlano(idDepartamentoSecao: number, idUnidade: number, ano: number): Observable<DepartamentoAprovarPlano[]> {
    return this.http
      .get<DepartamentoAprovarPlano[]>(
        `${this.baseUrl}/departamento/aprovar-plano/${idDepartamentoSecao}/${idUnidade}/${ano}`
      )
      .pipe(identity, this.errorHandlerService.catchError())
  }

  analisarPlano(id: number): Observable<DepartamentoAnalisarPlano> {
    return this.http
      .get<DepartamentoAnalisarPlano>(`${this.baseUrl}/departamento/aprovar-plano/${id}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  registrarAnalisePlano(id: number, form: DepartamentoAnalisarPlanoForm): Observable<StatusResponse> {
    return this.http
      .post<StatusResponse>(`${this.baseUrl}/departamento/aprovar-plano/${id}`, form)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  homologarRelatorio(
    idDepartamentoSecao: number,
    idUnidade: number,
    ano: number
  ): Observable<DepartamentoHomologarRelatorio[]> {
    return this.http
      .get<DepartamentoHomologarRelatorio[]>(
        `${this.baseUrl}/departamento/homologar-relatorio/${idDepartamentoSecao}/${idUnidade}/${ano}`
      )
      .pipe(identity, this.errorHandlerService.catchError())
  }

  analisarRelatorio(id: number): Observable<Relatorio> {
    return this.http
      .get<Relatorio>(`${this.baseUrl}/departamento/homologar-relatorio/${id}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  registrarAnaliseRelatorio(id: number, form: RelatorioForm): Observable<StatusResponse> {
    return this.http
      .post<StatusResponse>(`${this.baseUrl}/departamento/homologar-relatorio/${id}`, form)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  planoEntregue(idDepartamentoSecao: number, idUnidade: number, ano: number): Observable<PlanoEntregue[]> {
    return this.http
      .get<PlanoEntregue[]>(`${this.baseUrl}/departamento/plano-entregue/${idDepartamentoSecao}/${idUnidade}/${ano}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  relatorioEntregue(idDepartamentoSecao: number, idUnidade: number, ano: number): Observable<RelatorioEntregue[]> {
    return this.http
      .get<RelatorioEntregue[]>(
        `${this.baseUrl}/departamento/relatorio-entregue/${idDepartamentoSecao}/${idUnidade}/${ano}`
      )
      .pipe(identity, this.errorHandlerService.catchError())
  }

  statusInscricao(idDepartamentoSecao: number, idUnidade: number, ano: number): Observable<StatusInscricao[]> {
    return this.http
      .get<StatusInscricao[]>(
        `${this.baseUrl}/departamento/status-inscricao/${idDepartamentoSecao}/${idUnidade}/${ano}`
      )
      .pipe(identity, this.errorHandlerService.catchError())
  }

  disciplinaLiberada(
    idDepartamentoSecao: number,
    idUnidade: number,
    ano: number
  ): Observable<DepartamentoDisciplinaLiberada[]> {
    return this.http
      .get<DepartamentoDisciplinaLiberada[]>(
        `${this.baseUrl}/departamento/discipliona-liberada/${idDepartamentoSecao}/${idUnidade}/${ano}`
      )
      .pipe(identity, this.errorHandlerService.catchError())
  }

  download(folder: string, idInscricao: number): Observable<any> {
    let headerOptions = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
    })
    let requestOptions = { headers: headerOptions, responseType: 'blob' as 'blob' }
    return this.http
      .get(`${this.baseUrl}/departamento/download/${folder}/${idInscricao}`, requestOptions)
      .pipe(identity, this.errorHandlerService.catchError())
  }
}
